import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider, useStatutory } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { persons } from "../mock/statutory/mockData";
import { ChevronRight, Eye, RotateCcw, AlertCircle, User } from "lucide-react";
import { toast } from "sonner";

const CLASSIFICATION_FLOW = [
  { step: 1, question: "UAE national?" },
  { step: 2, question: "Pension jurisdiction?" },
  { step: 3, question: "Prior GPSSA coverage?" },
  { step: 4, question: "Employment / entrant date?" },
  { step: 5, question: "Sector type?" },
  { step: 6, question: "Regime assignment" },
];

function PersonCard({ person, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${isSelected ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/50"}`}
      data-testid={`person-card-${person.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{person.name}</span>
        </div>
        <span className="text-xs font-mono text-muted-foreground">{person.employeeId}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-muted-foreground">{person.nationality} · {person.workCountry}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs text-muted-foreground">{person.sector}</span>
      </div>
      <div className="mt-1.5">
        <StatutoryBadge state={person.nonSuccessState || person.eligibilityStatus} />
      </div>
    </button>
  );
}

function DecisionTree({ person }) {
  const steps = [
    { q: "UAE national?", a: person.nationality === "AE" ? "YES" : "NO — " + person.nationality },
    { q: "Work jurisdiction?", a: person.workCountry === "AE" ? "AE-" + (person.workEmirate || "Federal") : "IN — not applicable" },
    { q: "Prior GPSSA coverage?", a: person.regime === "UAE_LAW_7_1999" ? "YES — legacy joiner" : person.nationality === "AE" ? "NO — new entrant" : "N/A" },
    { q: "Employment sector?", a: person.sector || "PRIVATE" },
    { q: "Regime path?", a: person.regime || "UNRESOLVED — " + (person.nonSuccessState || "") },
    { q: "Eligibility lifecycle?", a: person.eligibilityStatus },
  ];
  return (
    <div className="space-y-1">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex flex-col items-center shrink-0 mt-1">
            <div className="h-4 w-4 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-[10px] font-bold text-primary">
              {i + 1}
            </div>
            {i < steps.length - 1 && <div className="w-px h-4 bg-border" />}
          </div>
          <div className="pb-1">
            <div className="text-xs text-muted-foreground">{step.q}</div>
            <div className="text-xs font-medium text-foreground">{step.a}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClassificationResult({ person }) {
  const isSuccess = !person.nonSuccessState && person.eligibilityStatus === "EFFECTIVE";

  return (
    <div className="space-y-4">
      {/* Result badge */}
      <div>
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Classification Result</div>
        {person.nonSuccessState ? (
          <StatutoryStatePanel
            state={person.nonSuccessState}
            reason={person.nonSuccessReason}
            oqRef={person.blockingOQ}
            suspensionNotice={["LEGAL_BASIS_PENDING", "COUNSEL_REVIEW_REQUIRED"].includes(person.nonSuccessState)}
          />
        ) : person.nonSuccessState === undefined && person.eligibilityStatus === "NO_APPLICABLE_REGIME" ? (
          <StatutoryStatePanel state="NO_APPLICABLE_REGIME" reason={person.nonSuccessReason} />
        ) : (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2">
              <StatutoryBadge state="EFFECTIVE" size="lg" />
            </div>
            <p className="text-sm text-green-800 mt-2">Regime determined and eligibility confirmed.</p>
          </div>
        )}
      </div>

      {/* Detail fields */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Classification Record</div>
        <dl className="space-y-2">
          {[
            ["Regime", person.regime || "—"],
            ["Article 2 Clause", person.article2Clause || "—"],
            ["Cohort", person.cohort || "—"],
            ["Employment Start", person.employmentStart],
            ["Eligibility Status", null],
          ].map(([key, val]) => (
            <div key={key} className="flex items-start justify-between gap-3">
              <dt className="text-xs text-muted-foreground shrink-0">{key}</dt>
              {val === null
                ? <dd><StatutoryBadge state={person.nonSuccessState || person.eligibilityStatus} /></dd>
                : <dd className="text-xs font-mono text-foreground text-right">{val}</dd>
              }
            </div>
          ))}
        </dl>
      </div>

      {/* Prototype actions */}
      <div className="space-y-2">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Prototype Actions</div>
        <div className="flex flex-wrap gap-2">
          {["View evidence", "View decision trace", "Request legal review"].map((action) => (
            <button
              key={action}
              onClick={() => toast.info(`${action} — prototype action (no backend)`)}
              className="text-xs border border-border rounded px-2.5 py-1.5 hover:bg-muted transition-colors flex items-center gap-1.5"
              data-testid={`action-${action.replace(/ /g, "-").toLowerCase()}`}
            >
              <Eye className="h-3 w-3" />
              {action}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">Prototype actions only — no backend calls.</p>
      </div>
    </div>
  );
}

function RegimeAssignmentContent() {
  const { currentRole } = useStatutory();
  const [selected, setSelected] = useState(persons[0]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Person List */}
      <div className="space-y-3">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Mock Persons</div>
        {persons.map((p) => (
          <PersonCard key={p.id} person={p} isSelected={selected?.id === p.id} onClick={() => setSelected(p)} />
        ))}
        <p className="text-[11px] text-muted-foreground italic">Mock prototype persons — not real employees.</p>
      </div>

      {/* Center: Decision Tree */}
      <div className="space-y-4">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Article 2 Decision Walkthrough</div>
        {selected ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="font-semibold text-sm mb-3">{selected.name}</div>
            <DecisionTree person={selected} />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Select a person to view classification trace.</div>
        )}
      </div>

      {/* Right: Result */}
      <div>
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Classification Result &amp; Evidence</div>
        {selected && <ClassificationResult person={selected} />}
      </div>
    </div>
  );
}

export default function StatutoryRegimeAssignment() {
  return (
    <StatutoryProvider>
      <PageHeader
        eyebrow="Statutory Configuration"
        title="Regime Assignment"
        description="Person-level Article 2 classification — mock prototype demonstration."
      />
      <StatutoryShell>
        <RegimeAssignmentContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
